import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight, ChevronLeft, ChevronRight, Map, MapPin, X } from "lucide-react";
import { caCities } from "@/data/site";
import { cityRegions } from "@/data/cityRegions";
import "leaflet/dist/leaflet.css";


import { batch1Meta } from "@/data/caCityBatch1";
import { batch2Meta } from "@/data/caCityBatch2";
import { batch3Meta } from "@/data/caCityBatch3";
import { batch4Meta } from "@/data/caCityBatch4";

const CITY_COORDS: Record<string, { lat: number; lng: number }> = {
  "spring-valley": { lat: 32.7448, lng: -117.0031 },
  "la-mesa": { lat: 32.7678, lng: -117.0231 },
  "el-cajon": { lat: 32.7948, lng: -116.9625 },
  "lemon-grove": { lat: 32.7425, lng: -117.0314 },
  "san-diego": { lat: 32.7157, lng: -117.1611 },
  "chula-vista": { lat: 32.6401, lng: -117.0842 },
  "santee": { lat: 32.8384, lng: -116.9739 },
  "la-presa": { lat: 32.7145, lng: -116.9961 },
  "national-city": { lat: 32.6781, lng: -117.0992 },
  "lakeside": { lat: 32.8573, lng: -116.9219 },
  "bonita": { lat: 32.6570, lng: -117.0364 },
  "rancho-san-diego": { lat: 32.7620, lng: -116.9364 },
  "jamul": { lat: 32.7186, lng: -116.8461 },
  "mission-valley": { lat: 32.7671, lng: -117.1450 },
  ...Object.fromEntries(
    [...batch1Meta, ...batch2Meta, ...batch3Meta, ...batch4Meta].map((c) => [c.slug, { lat: c.lat, lng: c.lng }]),
  ),
};

// Per-city label placement to avoid overlapping labels when pins are close.
type LabelPos = "right" | "left" | "top" | "bottom";
const LABEL_POS: Record<string, LabelPos> = {
  "lemon-grove": "left",
  "spring-valley": "right",
  "la-presa": "bottom",
  "la-mesa": "top",
  "bonita": "bottom",
  "national-city": "left",
};

const LABEL_SIDES: LabelPos[] = ["right", "left", "top", "bottom"];

/**
 * Keeps city labels readable when pins sit close together: each visible label
 * tries the four sides around its pin and is hidden when every side collides
 * with an already-placed label.
 */
function declutterLabels() {
  const wraps = Array.from(document.querySelectorAll<HTMLElement>(".mck-pin-wrap"));
  if (!wraps.length) return;
  // Active pin gets first pick so the selected city always keeps its label.
  wraps.sort((a, b) => Number(b.classList.contains("is-active")) - Number(a.classList.contains("is-active")));

  const placed: DOMRect[] = [];
  const overlaps = (r: DOMRect) =>
    placed.some((p) => r.left < p.right && r.right > p.left && r.top < p.bottom && r.bottom > p.top);

  wraps.forEach((wrap) => {
    const label = wrap.querySelector<HTMLElement>(".mck-pin-label");
    if (!label) return;
    wrap.classList.remove("mck-label-off");
    const preferred = LABEL_SIDES.find((s) => wrap.classList.contains(`mck-label-${s}`)) ?? "right";
    const order = [preferred, ...LABEL_SIDES.filter((s) => s !== preferred)];

    let fitted = false;
    for (const side of order) {
      LABEL_SIDES.forEach((s) => wrap.classList.remove(`mck-label-${s}`));
      wrap.classList.add(`mck-label-${side}`);
      const rect = label.getBoundingClientRect();
      if (!overlaps(rect)) {
        placed.push(rect);
        fitted = true;
        break;
      }
    }
    if (!fitted) {
      LABEL_SIDES.forEach((s) => wrap.classList.remove(`mck-label-${s}`));
      wrap.classList.add(`mck-label-${preferred}`, "mck-label-off");
    }
  });
}

export function RealisticCityMap() {

  const mapRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<string>("coronado");
  const [activeHub, setActiveHub] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const navigate = useNavigate();
  const leafletMapRef = useRef<any>(null);
  const markersRef = useRef<Record<string, any>>({});
  const hubLayerRef = useRef<any>(null);
  const cityLayersRef = useRef<Record<string, any>>({});
  const activeHubRef = useRef<string | null>(null);

  const cities = caCities.filter((c) => CITY_COORDS[c.slug]);

  // Regional hubs: one pin per hub on load, city pins revealed on click.
  const hubs = useMemo(() => {
    const bySlug: Record<string, (typeof cities)[number]> = Object.fromEntries(cities.map((c) => [c.slug, c]));
    return cityRegions
      .map((r) => {
        const hubCities = r.slugs.map((s) => bySlug[s]).filter(Boolean) as typeof cities;
        const lat = hubCities.reduce((a, c) => a + CITY_COORDS[c.slug].lat, 0) / (hubCities.length || 1);
        const lng = hubCities.reduce((a, c) => a + CITY_COORDS[c.slug].lng, 0) / (hubCities.length || 1);
        return { ...r, cities: hubCities, lat, lng };
      })
      .filter((h) => h.cities.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hubOf = (slug: string) => hubs.find((h) => h.cities.some((c) => c.slug === slug))?.id ?? null;
  const activeHubData = hubs.find((h) => h.id === activeHub) ?? null;
  const listCities = activeHubData ? activeHubData.cities : cities;
  const activeCity = cities.find((c) => c.slug === active) ?? cities[0];
  const activeIndex = listCities.findIndex((c) => c.slug === activeCity.slug);


  // Bring the map section into view (used when a pin is clicked).
  const scrollToMap = () => {
    const el = rootRef.current;
    if (!el || typeof window === "undefined") return;
    const rect = el.getBoundingClientRect();
    const target = Math.max(0, rect.top + window.scrollY - (window.innerHeight - rect.height) / 2 - 24);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const lenis = (window as unknown as {
      __lenis?: { scrollTo: (t: number, o?: { immediate?: boolean; force?: boolean }) => void } | null;
    }).__lenis;
    if (lenis) lenis.scrollTo(target, { immediate: reduced, force: true });
    else window.scrollTo({ top: target, behavior: reduced ? "auto" : "smooth" });
  };

  const selectCity = (slug: string) => {
    const hub = hubOf(slug);
    if (hub && hub !== activeHub) setActiveHub(hub);
    setActive(slug);
    setModalOpen(true);
    setPanelOpen(false);
    scrollToMap();
  };
  const selectHub = (id: string) => {
    setActiveHub(id);
    setModalOpen(false);
    setPanelOpen(false);
    scrollToMap();
  };
  const resetHubs = () => {
    setActiveHub(null);
    setModalOpen(false);
  };
  const goPrev = () =>
    selectCity(listCities[(activeIndex - 1 + listCities.length) % listCities.length].slug);
  const goNext = () => selectCity(listCities[(activeIndex + 1) % listCities.length].slug);


  const handleExplore = () => {
    // Build the transition overlay imperatively on document.body so it
    // survives the route change (a React portal here would unmount with
    // this component the moment we navigate).
    const overlay = document.createElement("div");
    overlay.className = "mck-explore-portal";
    overlay.setAttribute("data-active", "1");
    overlay.innerHTML = `
      <div class="mck-explore-shell">
        <div class="mck-explore-sweep"></div>
        <div class="mck-explore-center">
          <div class="mck-explore-text">
            <div class="mck-explore-eyebrow">Entering</div>
            <div class="mck-explore-city">${activeCity.name}</div>
            <div class="mck-explore-track"><div class="mck-explore-bar"></div></div>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    document.documentElement.style.overflow = "hidden";

    // Navigate while the overlay is still fully covering the screen, so
    // when the shell slides up the destination page is already underneath.
    window.setTimeout(() => {
      navigate({ to: "/areas/california/$city", params: { city: activeCity.slug } });
    }, 1400);

    // Remove the overlay after the slide-up finishes (1.4s delay + 0.8s).
    window.setTimeout(() => {
      overlay.remove();
      document.documentElement.style.overflow = "";
    }, 2400);
  };

  // Init map
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !mapRef.current || leafletMapRef.current) return;
      // Skip init when the container is hidden (e.g. mobile/tablet where the
      // desktop-only section uses `lg:block`). Leaflet cannot compute a
      // projection for a zero-size container and later flyTo() calls throw
      // "Invalid LatLng object: (NaN, NaN)", crashing the whole route.
      if (mapRef.current.clientWidth === 0 || mapRef.current.clientHeight === 0) return;

      const map = L.map(mapRef.current, {
        zoomControl: false,
        attributionControl: true,
        scrollWheelZoom: false,
        preferCanvas: true,
        minZoom: 6,
        maxZoom: 14,
        maxBoundsViscosity: 1.0,
        dragging: false,
        touchZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
      }).setView([32.75, -117.02], 11);

      // Lock the pan/zoom to California only
      const californiaBounds = L.latLngBounds(
        L.latLng(32.30, -124.75), // SW corner
        L.latLng(42.10, -114.05)  // NE corner
      );
      map.setMaxBounds(californiaBounds);

      // CARTO Voyager raster basemap (Basemaps API key, watermark-free).
      const cartoKey = import.meta.env['VITE_CARTO_API_KEY'] as string | undefined;
      L.tileLayer(
        `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=${cartoKey}`,
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: "abcd",
          maxZoom: 20,
        },
      ).addTo(map);






      // Frame the whole coverage footprint so every hub is visible.
      const allCoords = cities.map((c) => L.latLng(CITY_COORDS[c.slug].lat, CITY_COORDS[c.slug].lng));
      if (allCoords.length) {
        map.fitBounds(L.latLngBounds(allCoords), { padding: [70, 70] });
      }

      // Hub pins (shown first) - one per region.
      const hubLayer = L.layerGroup().addTo(map);
      hubs.forEach((h) => {
        const icon = L.divIcon({
          className: "",
          html: `
            <div class="mck-hub-wrap" data-hub="${h.id}">
              <span class="mck-hub-ring"></span>
              <span class="mck-hub-core"></span>
              <span class="mck-hub-label">${h.label} Hub</span>
            </div>
          `,
          iconSize: [16, 16],
          iconAnchor: [8, 8],
        });
        const m = L.marker([h.lat, h.lng], { icon, interactive: true }).addTo(hubLayer);
        m.on("click", () => selectHub(h.id));
      });
      hubLayerRef.current = hubLayer;

      // City pins, grouped per hub so a hub click reveals only its cities.
      hubs.forEach((h) => {
        const layer = L.layerGroup();
        h.cities.forEach((c) => {
          const coord = CITY_COORDS[c.slug];
          const isHQ = c.slug === "spring-valley";
          const icon = L.divIcon({
            className: "",
            html: `
              <div class="mck-pin-wrap mck-label-${LABEL_POS[c.slug] ?? "right"}${isHQ ? " mck-pin-hq" : ""}" data-slug="${c.slug}">
                ${isHQ ? "" : '<span class="mck-pin-ring"></span><span class="mck-pin-dot"></span>'}
                ${isHQ ? '<span class="mck-pin-hq-star" aria-hidden="true">★</span>' : ""}
                <span class="mck-pin-label">${isHQ ? c.name + " (Main)" : c.name}</span>
              </div>
            `,
            iconSize: isHQ ? [22, 22] : [12, 12],
            iconAnchor: isHQ ? [11, 11] : [6, 6],
          });
          const m = L.marker([coord.lat, coord.lng], { icon }).addTo(layer);
          m.on("click", () => {
            setActive(c.slug);
            setModalOpen(true);
            scrollToMap();
          });
          markersRef.current[c.slug] = m;
        });
        cityLayersRef.current[h.id] = layer;
      });

      leafletMapRef.current = map;
      map.on("zoomend moveend", () => declutterLabels());
      setReady(true);
    })();


    return () => {
      cancelled = true;
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the map sized correctly on orientation change / resize.
  useEffect(() => {
    const onResize = () => leafletMapRef.current?.invalidateSize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Start with the city drawer collapsed on small screens.
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 640) setPanelOpen(false);
  }, []);

  const didInitialFly = useRef(false);

  // Swap between hub pins and the city pins of the selected hub.
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!ready || !map) return;
    activeHubRef.current = activeHub;

    // Hide every city layer, then show the active hub's cities.
    Object.entries(cityLayersRef.current).forEach(([id, layer]) => {
      if (id === activeHub) {
        if (!map.hasLayer(layer)) layer.addTo(map);
      } else if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });

    const hubLayer = hubLayerRef.current;
    if (hubLayer) {
      if (activeHub) {
        if (map.hasLayer(hubLayer)) map.removeLayer(hubLayer);
      } else if (!map.hasLayer(hubLayer)) {
        hubLayer.addTo(map);
      }
    }

    const hub = hubs.find((h) => h.id === activeHub);
    if (hub) {
      map.flyToBounds(
        hub.cities.map((c) => [CITY_COORDS[c.slug].lat, CITY_COORDS[c.slug].lng] as [number, number]),
        { padding: [90, 90], duration: 1.0, maxZoom: 12 },
      );
    } else if (didInitialFly.current) {
      map.flyToBounds(
        cities.map((c) => [CITY_COORDS[c.slug].lat, CITY_COORDS[c.slug].lng] as [number, number]),
        { padding: [70, 70], duration: 1.0 },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHub, ready]);

  // Zoom to the selected city. Skipped on first run so the coverage framing
  // stays visible until the visitor actually picks something.
  useEffect(() => {
    if (!ready || !leafletMapRef.current) return;
    const coord = CITY_COORDS[active];
    if (!coord) return;
    if (!didInitialFly.current) {
      didInitialFly.current = true;
    } else if (modalOpen) {
      leafletMapRef.current.flyTo([coord.lat, coord.lng], 12, { duration: 1.1 });
    }

    Object.entries(markersRef.current).forEach(([slug, marker]) => {
      const el: HTMLElement | undefined = marker.getElement()?.querySelector(".mck-pin-wrap");
      if (el) {
        el.classList.toggle("is-active", slug === active);
      }
    });
    const t = window.setTimeout(() => declutterLabels(), 60);
    return () => window.clearTimeout(t);
  }, [active, ready, modalOpen, activeHub]);



  return (
    <div ref={rootRef} className="relative h-[68vh] min-h-[420px] w-full overflow-hidden rounded-2xl border border-primary/10 bg-white shadow-[0_30px_70px_-20px_rgba(0,51,102,0.18)] sm:h-[72vh] sm:rounded-3xl md:h-[82vh] md:shadow-[0_60px_120px_-20px_rgba(0,51,102,0.18)]">
      {/* Map surface */}
      <div ref={mapRef} className="absolute inset-0 h-full w-full" aria-label="Realistic map of California service cities" />

      {/* Soft edge vignette for light theme */}
      <div className="pointer-events-none absolute inset-0 z-[400] bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(0,51,102,0.12)_100%)]" />

      {/* HUD corners */}
      <div className="hidden sm:block">
        <HudCorner className="left-4 top-4" corner="tl" />
        <HudCorner className="right-4 top-4" corner="tr" />
        <HudCorner className="left-4 bottom-4" corner="bl" />
        <HudCorner className="right-4 bottom-4" corner="br" />
      </div>

      {/* Game-style startup popup – inside the map, top-left */}
      <div className="pointer-events-none absolute left-3 top-3 z-[430] max-w-[58%] text-left sm:left-8 sm:top-8 sm:max-w-sm">
        <div className="mck-intro-popup">
          <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-secondary sm:text-[11px] sm:tracking-[0.32em]">
            Southern California · Live Coverage
          </div>
          <h2 className="mt-1.5 font-serif text-xl font-extrabold leading-tight text-primary sm:mt-3 sm:text-3xl xl:text-4xl">
            Where we work.
          </h2>
          <p className="mt-2 hidden text-sm text-primary/75 sm:block">
            Each pin is a region we run crews in. Click a region to light up its cities, then pick one to open its local page.
          </p>
        </div>
      </div>

      {/* Back to regions control */}
      {activeHub && (
        <button
          type="button"
          onClick={resetHubs}
          className="absolute left-3 bottom-3 z-[440] inline-flex items-center gap-2 rounded-full border border-primary/15 bg-white/90 px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-primary shadow-lg backdrop-blur-xl transition hover:bg-primary/5 sm:left-8 sm:bottom-8 sm:text-xs"
        >
          <ArrowLeft className="h-4 w-4" /> All Regions
        </button>
      )}

      {/* Floating map icon – opens the All Service Cities panel */}
      {!panelOpen && (
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          aria-label="Show all service cities"
          className="absolute right-2 top-2 z-[440] inline-flex h-10 w-10 items-center justify-center rounded-full border border-primary/15 bg-white/90 text-primary shadow-lg backdrop-blur-xl transition hover:bg-primary/5 sm:right-4 sm:top-4 sm:h-11 sm:w-11"
        >
          <Map className="h-5 w-5" />
        </button>
      )}

      {/* All Service Cities panel – docked to the upper part of the map */}
      {panelOpen && (
        <div className="absolute right-2 top-2 z-[440] w-[190px] sm:w-[230px] lg:right-4 lg:top-4 lg:w-[250px]">
          <div className="mck-cities-panel relative rounded-2xl border border-primary/10 bg-white/90 p-3 shadow-2xl backdrop-blur-xl lg:p-4">
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              aria-label="Hide city list"
              className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-primary/15 bg-white text-primary/80 shadow-sm transition hover:bg-primary/5"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <div className="pr-6 text-[9px] font-bold uppercase tracking-[0.22em] text-secondary sm:text-[10px] sm:tracking-[0.3em]">
              {activeHubData ? activeHubData.label : "Service Regions"}
            </div>
            {activeHubData && (
              <button
                type="button"
                onClick={resetHubs}
                className="mt-2 inline-flex items-center gap-1 rounded-full border border-primary/15 bg-white px-2.5 py-1 text-[10px] font-bold text-primary/80 transition hover:bg-primary/5"
              >
                <ArrowLeft className="h-3 w-3" /> All regions
              </button>
            )}
            <div
              data-lenis-prevent
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              className="mck-cities-scroll mt-2 flex max-h-[30vh] flex-col gap-1.5 overflow-y-auto overscroll-contain pr-6 lg:max-h-[46vh]"
              style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
            >
              {!activeHubData
                ? hubs.map((h) => (
                    <button
                      key={h.id}
                      type="button"
                      onClick={() => selectHub(h.id)}
                      className="inline-flex items-center justify-between gap-1 rounded-xl border border-primary/10 bg-primary/[0.03] px-2.5 py-1.5 text-left text-[10px] font-semibold text-primary/85 transition-all hover:border-secondary/60 hover:text-primary lg:text-[11px]"
                    >
                      <span className="flex items-center gap-1">
                        <MapPin className="h-2.5 w-2.5 shrink-0" /> {h.label}
                      </span>
                      <span className="rounded-full bg-secondary/15 px-1.5 py-0.5 text-[9px] font-bold text-secondary">
                        {h.cities.length}
                      </span>
                    </button>
                  ))
                : activeHubData.cities.map((c) => (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => selectCity(c.slug)}
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-semibold transition-all lg:text-[11px] ${
                        c.slug === active && modalOpen
                          ? "border-secondary bg-secondary text-secondary-foreground"
                          : "border-primary/10 bg-primary/[0.03] text-primary/85 hover:border-secondary/60 hover:text-primary"
                      }`}
                    >
                      <MapPin className="h-2.5 w-2.5" /> {c.name}
                    </button>
                  ))}
            </div>

          </div>
        </div>
      )}

      {/* Bottom-center dramatic reveal for selected city */}
      {modalOpen && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[450] flex justify-center px-3 pb-8 sm:px-6 sm:pb-14">
          {/* Soft bottom highlight glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-[45%] bg-[radial-gradient(ellipse_at_bottom,color-mix(in_oklab,var(--secondary)_22%,transparent)_0%,rgba(255,255,255,0.75)_55%,transparent_100%)]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-white/90 via-white/50 to-transparent"
          />

          <div key={activeCity.slug} className="mck-city-reveal relative flex max-w-3xl flex-col items-center text-center">
            {/* Soft elliptical shadow plate behind the reveal to lift it off the map */}
            <div
              aria-hidden
              className="pointer-events-none absolute left-1/2 top-1/2 -z-[1] h-[130%] w-[130%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-white/70 blur-2xl shadow-[0_30px_80px_-10px_rgba(0,51,102,0.35)]"
            />
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              aria-label="Close"
              className="pointer-events-auto absolute -top-10 right-0 inline-flex h-8 w-8 items-center justify-center rounded-full border border-primary/20 bg-white/80 text-primary/80 shadow-md backdrop-blur-md transition hover:bg-primary/5"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="text-[9px] font-bold uppercase tracking-[0.35em] text-secondary">
              Selected City
            </div>
            <h3 className="mt-1.5 font-serif text-2xl font-extrabold uppercase tracking-tight text-primary sm:text-3xl xl:text-4xl">
              {activeCity.name}
            </h3>
            <p className="mt-2 line-clamp-3 max-w-md text-[11px] leading-relaxed text-primary/75 sm:text-xs">
              {activeCity.blurb}
            </p>

            <div className="pointer-events-auto mt-4 flex items-center justify-center gap-2 sm:mt-6 sm:gap-3">
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous city"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-primary/15 bg-white/85 text-primary shadow-md backdrop-blur-md transition hover:bg-primary/5 sm:h-11 sm:w-11"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={handleExplore}
                className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2.5 text-xs font-bold text-secondary-foreground shadow-[0_10px_30px_-5px_color-mix(in_oklab,var(--secondary)_45%,transparent)] transition-transform hover:-translate-y-0.5 sm:px-6 sm:py-3 sm:text-sm"
              >
                Explore {activeCity.name} <ArrowUpRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next city"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-primary/15 bg-white/85 text-primary shadow-md backdrop-blur-md transition hover:bg-primary/5 sm:h-11 sm:w-11"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pin styles + animations */}
      <style>{`
        .mck-cities-scroll { scrollbar-width: thin; scrollbar-color: color-mix(in oklab, var(--primary) 35%, transparent) transparent; }
        .mck-cities-scroll::-webkit-scrollbar { width: 6px; }
        .mck-cities-scroll::-webkit-scrollbar-thumb { background: color-mix(in oklab, var(--primary) 35%, transparent); border-radius: 9999px; }
        .mck-cities-scroll::-webkit-scrollbar-track { background: transparent; }
        @keyframes mck-city-reveal {
          0%   { opacity: 0; transform: translateY(80px) scale(0.92); filter: blur(8px); letter-spacing: 0.2em; }
          55%  { opacity: 1; transform: translateY(-6px) scale(1.02); filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); letter-spacing: normal; }
        }
        .mck-city-reveal { animation: mck-city-reveal .7s cubic-bezier(.2,.85,.25,1.15) both; }
        @keyframes mck-explore-sweep {
          0%   { clip-path: circle(0% at 50% 60%); }
          100% { clip-path: circle(150% at 50% 60%); }
        }
        .mck-explore-sweep { animation: mck-explore-sweep .5s cubic-bezier(.7,0,.25,1) forwards; }
        @keyframes mck-explore-text {
          0%   { opacity: 0; transform: translateY(20px) scale(0.9); filter: blur(10px); }
          40%  { opacity: 0; }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        .mck-explore-text { animation: mck-explore-text .55s ease-out forwards; }
        @keyframes mck-explore-bar {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(0); }
        }
        .mck-explore-bar { animation: mck-explore-bar .5s ease-out .1s forwards; transform: translateX(-100%); }
        @keyframes mck-explore-exit {
          0%   { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(-105%); opacity: 1; }
        }
        .mck-explore-shell { animation: mck-explore-exit .8s cubic-bezier(.65,0,.2,1) 1.4s forwards; }
        @keyframes mck-pulse {
          0% { transform: scale(1); opacity: 0.7; }
          80% { transform: scale(3.4); opacity: 0; }
          100% { transform: scale(3.4); opacity: 0; }
        }
        @keyframes mck-hub-pulse {
          0% { transform: scale(1); opacity: 0.55; }
          80% { transform: scale(2.2); opacity: 0; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        .mck-hub-wrap {
          position: relative;
          width: 16px; height: 16px;
          display: block;
          cursor: pointer;
        }
        .mck-hub-core {
          position: absolute; inset: 0;
          border-radius: 9999px;
          background: var(--primary);
          box-shadow: 0 0 0 2px rgba(255,255,255,0.95), 0 6px 14px -3px rgba(0,51,102,0.45);
          transition: transform .2s ease;
        }
        .mck-hub-wrap:hover .mck-hub-core { transform: scale(1.12); }
        .mck-hub-ring {
          position: absolute; inset: -1px;
          border-radius: 9999px;
          background: var(--secondary);
          opacity: .4;
          animation: mck-hub-pulse 2.2s ease-out infinite;
        }
        .mck-hub-label {
          position: absolute;
          left: 50%;
          top: 22px;
          transform: translateX(-50%);
          white-space: nowrap;
          font-size: 10px;
          font-weight: 900;
          color: #0b1220;
          pointer-events: none;
          text-shadow: 0 0 2px #fff, 0 0 4px #fff, 0 0 8px rgba(255,255,255,0.95);
        }
        .mck-pin-wrap {
          position: relative;
          width: 12px; height: 12px;
          display: block;
        }
        .mck-pin-dot {
          position: absolute; inset: 0;
          border-radius: 9999px;
          background: var(--secondary);
          box-shadow: 0 0 0 2px rgba(255,255,255,0.95), 0 0 16px 2px color-mix(in oklab, var(--secondary) 60%, transparent);
          transition: transform .25s ease;
        }
        .mck-pin-ring {
          position: absolute; inset: 0;
          border-radius: 9999px;
          background: var(--secondary);
          opacity: .55;
          animation: mck-pulse 2s ease-out infinite;
        }
        .mck-pin-label {
          position: absolute;
          white-space: nowrap;
          font-size: 12.5px;
          font-weight: 900;
          letter-spacing: 0.02em;
          color: #0b1220;
          text-shadow:
            0 0 2px #fff,
            0 0 4px #fff,
            0 0 8px rgba(255,255,255,0.95),
            0 1px 2px rgba(255,255,255,1),
            0 0 14px color-mix(in oklab, var(--secondary) 55%, transparent);
          opacity: 1;
          transition: font-size .2s ease, text-shadow .2s ease;
          pointer-events: none;
        }
        .mck-label-right .mck-pin-label { left: 16px; top: -6px; }
        .mck-label-left  .mck-pin-label { right: 16px; top: -6px; }
        .mck-label-top   .mck-pin-label { left: 50%; bottom: 18px; transform: translateX(-50%); }
        .mck-label-bottom .mck-pin-label { left: 50%; top: 18px; transform: translateX(-50%); }
        .mck-pin-wrap.mck-label-off .mck-pin-label { opacity: 0; pointer-events: none; }
        .mck-pin-wrap.mck-label-off:hover .mck-pin-label,
        .mck-pin-wrap.mck-label-off.is-active .mck-pin-label { opacity: 1; }

        .mck-pin-wrap.is-active .mck-pin-dot {
          transform: scale(1.35);
          box-shadow: 0 0 0 3px rgba(255,255,255,0.98), 0 0 24px 6px color-mix(in oklab, var(--secondary) 80%, transparent);
        }
        .mck-pin-wrap.is-active .mck-pin-label {
          font-size: 14.5px;
          text-shadow:
            0 0 3px rgba(255,255,255,1),
            0 0 8px rgba(255,255,255,0.95),
            0 0 18px color-mix(in oklab, var(--secondary) 75%, transparent);
        }
        /* Headquarters pin - Carlsbad (star only) */
        .mck-pin-hq {
          width: 30px !important;
          height: 30px !important;
          filter: drop-shadow(0 0 10px color-mix(in oklab, var(--primary) 60%, transparent));
        }
        .mck-pin-hq::before {
          content: "";
          position: absolute;
          inset: -6px;
          border-radius: 9999px;
          background: var(--primary);
          opacity: .55;
          animation: mck-pulse 1.6s ease-out infinite;
          z-index: -1;
        }
        .mck-pin-hq-star {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          font-size: 26px;
          line-height: 1;
          color: var(--primary);
          pointer-events: none;
          text-shadow: 0 0 3px #fff, 0 0 6px #fff, 0 1px 3px rgba(255,255,255,0.9);
        }
        .mck-pin-hq .mck-pin-label {
          color: var(--primary);
          font-size: 13.5px;
          text-shadow:
            0 0 2px #fff,
            0 0 4px #fff,
            0 0 8px rgba(255,255,255,0.95),
            0 1px 2px rgba(255,255,255,1),
            0 0 14px color-mix(in oklab, var(--primary) 45%, transparent);
        }
        .mck-pin-hq.is-active {
          filter: drop-shadow(0 0 16px color-mix(in oklab, var(--primary) 80%, transparent));
        }
        .mck-pin-hq.is-active .mck-pin-hq-star {
          font-size: 32px;
        }
        .mck-pin-hq.is-active .mck-pin-label {
          font-size: 15.5px;
        }
        .leaflet-container {
          background: #f3f4f6 !important;
          font-family: inherit;
        }
        .leaflet-control-attribution {
          background: rgba(255,255,255,0.75) !important;
          color: rgba(0,51,102,0.65) !important;
          font-size: 10px !important;
          padding: 2px 8px !important;
        }
        .leaflet-control-attribution a { color: rgba(0,51,102,0.9) !important; }
      `}</style>
    </div>
  );
}

function HudCorner({ className, corner }: { className?: string; corner: "tl" | "tr" | "bl" | "br" }) {
  const rotate = { tl: "0deg", tr: "90deg", br: "180deg", bl: "270deg" }[corner];
  return (
    <div
      className={`pointer-events-none absolute z-[405] h-10 w-10 ${className ?? ""}`}
      style={{ transform: `rotate(${rotate})` }}
    >
      <div className="absolute left-0 top-0 h-[2px] w-6 bg-primary/60" />
      <div className="absolute left-0 top-0 h-6 w-[2px] bg-primary/60" />
    </div>
  );
}